import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { CampaignIcon } from "../../icons/customIcons";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  AssociatedCampaignSync,
  useCreateAssociatedCampaignSyncMutation,
} from "../../store/apis/briefingApi";
import {
  useGetCampaignByIdQuery,
  useGetUserDbCampaignsQuery,
} from "../../store/apis/campaignApi";
import { useGetInsertionOrdersByCampaignQuery } from "../../store/apis/insertionOrderApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import SectionCard from "./inline/SectionCard";
import FieldGroup from "./inline/FieldGroup";
import InlineField from "./inline/InlineField";
import { InlineText, SectionEmpty } from "./inline/inputs";
import { bindField, useInlineSection } from "./inline/useInlineSection";
import { extractApiErrorMessage } from "./inline/apiErrors";

const DV360_PLATFORMS = [
  "BEAPOLO_DISNEY_PLUS",
  "BEAPOLO_MOVISTAR",
  "BEAPOLO_NETFLIX",
  "BEAPOLO_OTT",
  "BEAPOLO_SAMSUNG_TV",
  "BEAPOLO_YOUTUBE_CTV",
  "DV360",
];

interface SyncDraft {
  beapoloCampaignId: string;
  internalCampaignId: string;
  insertionOrderId: string;
  isLinked: boolean;
}

const EMPTY_SYNC: SyncDraft = {
  beapoloCampaignId: "",
  internalCampaignId: "",
  insertionOrderId: "",
  isLinked: false,
};

type Option = { id: string; displayName?: string };

/** Name of an entity with its ID underneath. */
const NamedId: React.FC<{ name?: string; id: string }> = ({ name, id }) => (
  <Stack spacing={0.25}>
    {name && <span>{name}</span>}
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
    >
      {id}
    </Typography>
  </Stack>
);

interface AssociatedCampaignSyncSectionProps {
  briefingId: string;
  associatedId: string;
  platform: string;
  sync: AssociatedCampaignSync | null;
  /** Internal campaign the line is linked to in production. */
  linkedInternalCampaignId?: string | null;
  onOpenLinkedCampaign?: (parentId: string, internalId: string) => void;
  canEdit: boolean;
  /** Reloads the campaign; resolves once fresh data is in. */
  onSaved: () => Promise<unknown>;
}

const AssociatedCampaignSyncSection: React.FC<
  AssociatedCampaignSyncSectionProps
> = ({
  briefingId,
  associatedId,
  platform,
  sync,
  linkedInternalCampaignId,
  onOpenLinkedCampaign,
  canEdit,
  onSaved,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [creating, setCreating] = useState(false);
  const [createSync] = useCreateAssociatedCampaignSyncMutation();
  const isDv360 = DV360_PLATFORMS.includes(platform);

  const base = useMemo<SyncDraft>(
    () =>
      sync && !creating
        ? {
            beapoloCampaignId: sync.beapoloCampaignId ?? "",
            internalCampaignId: sync.internalCampaignId ?? "",
            insertionOrderId: sync.insertionOrderId ?? "",
            isLinked: sync.isLinked ?? false,
          }
        : EMPTY_SYNC,
    [sync, creating]
  );

  const handleSave = async (draft: SyncDraft) => {
    try {
      await createSync({
        briefingId,
        associatedId,
        beapoloCampaignId: draft.beapoloCampaignId || null,
        internalCampaignId: draft.internalCampaignId || null,
        insertionOrderId: draft.insertionOrderId || null,
        isLinked: draft.isLinked,
      }).unwrap();
      await onSaved();
      setCreating(false);
      dispatch(
        setToastMessage({
          message: t("briefingsEdit.syncUpdateSuccess", "Sync status updated"),
          severity: "success",
        })
      );
      return true;
    } catch (error) {
      dispatch(
        setToastMessage({
          message:
            extractApiErrorMessage(error) ??
            t("briefingsEdit.syncUpdateError", "Failed to update sync status"),
          severity: "error",
        })
      );
      return false;
    }
  };

  const section = useInlineSection<SyncDraft>({
    id: "sync",
    base,
    onSave: handleSave,
    forceEdit: creating,
    readOnly: !canEdit || (!sync && !creating),
  });
  const { draft } = section;
  const hasSync = Boolean(sync) || creating;

  // Names for the IDs (and the options while editing).
  const { data: campaignsData, isLoading: isLoadingCampaigns } =
    useGetUserDbCampaignsQuery(undefined, { skip: !hasSync });
  const campaigns = (campaignsData?.data ?? []) as unknown as Option[];
  const { data: insertionOrdersData, isLoading: isLoadingIOs } =
    useGetInsertionOrdersByCampaignQuery(draft.beapoloCampaignId, {
      skip: !isDv360 || !draft.beapoloCampaignId,
    });
  const insertionOrders = (insertionOrdersData?.data ?? []) as Option[];
  const { data: campaignDetails, isLoading: isLoadingDetails } =
    useGetCampaignByIdQuery(
      { campaignId: draft.beapoloCampaignId },
      { skip: isDv360 || !draft.beapoloCampaignId }
    );
  const internalCampaigns = (campaignDetails?.data?.internalCampaigns ??
    []) as unknown as Option[];

  const nameOf = (options: Option[], id: string) =>
    options.find((option) => option.id === id)?.displayName;

  const picker = (
    options: Option[],
    value: string,
    onChange: (id: string) => void,
    loading: boolean,
    disabled = false
  ) => (
    <Autocomplete<Option>
      fullWidth
      size="small"
      options={options}
      getOptionLabel={(option) => option.displayName || option.id}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      value={options.find((option) => option.id === value) ?? null}
      onChange={(_event, next) => onChange(next?.id ?? "")}
      loading={loading}
      disabled={disabled}
      renderInput={(params) => (
        <InlineText
          {...params}
          placeholder={
            disabled
              ? t(
                  "briefingsView.sync.selectCampaignFirst",
                  "Select a campaign first"
                )
              : t("common.select", "Select...")
          }
        />
      )}
    />
  );

  const childKey = isDv360 ? "insertionOrderId" : "internalCampaignId";
  const childOptions = isDv360 ? insertionOrders : internalCampaigns;
  const childId = draft[childKey];

  return (
    <SectionCard
      icon={<SyncAltIcon />}
      title={t("briefingsView.syncSectionTitle", "Linking status")}
      subtitle={t(
        "briefingsView.syncSectionSubtitle",
        "Vinculación con la campaña en producción"
      )}
      dirtyCount={section.dirtyCount}
      saving={section.isSaving}
      saveLabel={
        creating ? t("briefingsView.createSync", "Link campaign") : undefined
      }
      onSave={() => void section.save()}
      onDiscard={() => {
        section.discard();
        setCreating(false);
      }}
      createdAt={creating ? null : sync?.createdAt}
      updatedAt={creating ? null : sync?.updatedAt}
      actions={
        linkedInternalCampaignId &&
        (sync?.beapoloCampaignId ? (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<LinkIcon />}
            endIcon={<OpenInNewIcon />}
            onClick={() =>
              onOpenLinkedCampaign?.(
                sync.beapoloCampaignId as string,
                linkedInternalCampaignId
              )
            }
          >
            {t("briefingsView.viewCampaign", "View campaign")}
          </Button>
        ) : (
          <Tooltip
            title={t(
              "briefingsView.brokenLink",
              "Broken Link - Parent Campaign mapping is missing"
            )}
          >
            <Chip
              size="small"
              icon={<WarningAmberIcon />}
              label={t("briefingsView.brokenLinkText", "Broken Link")}
              color="warning"
              variant="outlined"
            />
          </Tooltip>
        ))
      }
    >
      {!hasSync ? (
        <SectionEmpty
          message={t(
            "briefingsView.syncNotFound",
            "This associated campaign is not linked yet."
          )}
          actionLabel={
            canEdit ? t("briefingsView.createSync", "Link campaign") : undefined
          }
          onAction={() => setCreating(true)}
        />
      ) : (
        <Stack spacing={2}>
          {linkedInternalCampaignId && !sync?.beapoloCampaignId && (
            <Alert severity="warning" variant="outlined">
              {t(
                "briefingsView.brokenLink",
                "Broken Link - Parent Campaign mapping is missing"
              )}
            </Alert>
          )}
          <FieldGroup columns={3}>
            <InlineField
              label={t(
                "briefingsView.sync.beapoloCampaignId",
                "BeApolo campaign"
              )}
              icon={<CampaignIcon color="currentColor" size={16} />}
              empty={!draft.beapoloCampaignId}
              copyValue={draft.beapoloCampaignId}
              value={
                <NamedId
                  name={nameOf(campaigns, draft.beapoloCampaignId)}
                  id={draft.beapoloCampaignId}
                />
              }
              input={picker(
                campaigns,
                draft.beapoloCampaignId,
                (id) =>
                  section.setFields({
                    beapoloCampaignId: id,
                    internalCampaignId: "",
                    insertionOrderId: "",
                  }),
                isLoadingCampaigns
              )}
              {...bindField(section, "beapoloCampaignId", canEdit, () =>
                section.cancelField("beapoloCampaignId", [
                  "internalCampaignId",
                  "insertionOrderId",
                ])
              )}
            />
            <InlineField
              label={
                isDv360
                  ? t("briefingsView.sync.insertionOrderId", "Insertion order")
                  : t(
                      "briefingsView.sync.internalCampaignId",
                      "Internal campaign"
                    )
              }
              icon={<AccountTreeOutlinedIcon />}
              empty={!childId}
              copyValue={childId}
              value={
                <NamedId name={nameOf(childOptions, childId)} id={childId} />
              }
              input={picker(
                childOptions,
                childId,
                (id) => section.setField(childKey, id),
                isDv360 ? isLoadingIOs : isLoadingDetails,
                !draft.beapoloCampaignId
              )}
              {...bindField(section, childKey, canEdit)}
            />
            <InlineField
              label={t("briefingsView.sync.isLinked", "Linked status")}
              icon={<LinkIcon />}
              value={
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      display: "inline-block",
                      bgcolor: draft.isLinked
                        ? "success.main"
                        : "text.disabled",
                    }}
                  />
                  <span>
                    {draft.isLinked
                      ? t("briefingsView.sync.linked", "Linked")
                      : t("briefingsView.sync.notLinked", "Not linked")}
                  </span>
                </Stack>
              }
              input={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch
                    size="small"
                    color="success"
                    checked={draft.isLinked}
                    onChange={(event) =>
                      section.setField("isLinked", event.target.checked)
                    }
                  />
                  <Typography variant="body2">
                    {draft.isLinked
                      ? t("briefingsView.sync.linked", "Linked")
                      : t("briefingsView.sync.notLinked", "Not linked")}
                  </Typography>
                </Stack>
              }
              {...bindField(section, "isLinked", canEdit)}
            />
          </FieldGroup>
        </Stack>
      )}
    </SectionCard>
  );
};

export default AssociatedCampaignSyncSection;
